// Năm thẻ việc của kế toán: "Tiền về chưa rõ đơn", "Phiếu chờ duyệt",
// "Cần xem lại", "Sổ ngân hàng", "Cần xử lý bên Kế toán" (QĐ 0311).
//
// Nhãn thẻ lấy từ từ điển nhãn của màn — xem `../../payment-guide-tabs.ts`.

import { TAB_LABEL } from "@/features/payments/labels";
import type { GuideTab } from "../../types";

export const TABS_DOI_CHIEU: GuideTab[] = [
  {
    key: "queue",
    label: TAB_LABEL.queue,
    purpose: "Hàng đợi tiền đã về tài khoản nhưng chưa biết của đơn nào.",
    where: [
      'Ba nút lọc trên đầu: "Chưa xử lý" (mở sẵn), "Đã nhận hôm nay", "Đã bỏ qua".',
      'Ô tìm nhận số tiền, nội dung chuyển khoản hoặc mã tham chiếu. Số tiền gõ như in trên màn cũng được — có dấu chấm, dấu phẩy, chữ "đ" hay chữ "k" (nghìn) ở cuối đều tìm ra.',
      'Khi có khoản máy khớp chắc: dải xanh trên đầu ghi "… khoản máy khớp chắc" kèm nút "✓ Nhận … khoản khớp chắc".',
      'Dòng chữ nhỏ "Sổ ngân hàng nhận khoản mới nhất lúc …" ngay trên danh sách.',
      'Cạnh ô tìm: nút "+ Thêm giao dịch ngân hàng" để gõ tay một khoản máy chưa nhận được.',
      "Mỗi khoản là một thẻ riêng: số tiền in đậm, thời điểm tiền về, ngân hàng kèm 4 số cuối tài khoản nhận, và nội dung khách gõ để nguyên văn — phần chữ máy dựa vào để gợi ý được tô vàng.",
      "Dưới nội dung là dải tín hiệu: 🙋 có người báo · ✅ Khớp chắc / 🤖 Máy gợi ý (tối đa 5 đơn, mỗi đơn một dòng) · 📝 có phiếu ghi tay chờ nối · ✅ đã nhận vào đơn.",
      'Cuối dòng 🙋 có "🧾 Xem bill sale gửi" (bấm mở ảnh bill khách gửi) hoặc chữ đỏ "Chưa có bill": vẫn nhận được, nhưng là đang duyệt không có bill.',
      'Mỗi dòng gợi ý ghi mã đơn, tên khách, đơn còn nợ bao nhiêu (khoản này dư hay còn thiếu bao nhiêu), ngày tạo đơn, và các nhãn dấu hiệu: "Trùng số điện thoại", "Trùng mã đơn", "Trùng tên khách", "Trùng một phần tên", "Có GNPF + 4 số cuối SĐT", "Đúng số còn nợ". Từ đơn thứ hai trở đi có nút "Nhận vào đơn này".',
      'Cuối thẻ các nút luôn cùng thứ tự: "✓ Nhận vào đơn …" · "🔗 Nối vào phiếu đó" (khi có phiếu ghi tay) · "Chọn đơn khác" (luôn có) · "Không phải của mình".',
      'Cuối danh sách: nút "Xem thêm" kèm dòng "Đang hiện … khoản" khi hàng đợi còn khoản chưa tải.',
      'Dòng chữ nhỏ dưới hàng chip ngày ghi "Lọc theo ngày tiền về tài khoản".',
    ],
    howItWorks: [
      "Khoản xếp theo mức chắc chắn chứ không theo thời gian: có người báo lên đầu, rồi khoản khớp chắc, rồi máy gợi ý, rồi khoản có phiếu ghi tay, cuối cùng là khoản chưa manh mối. Mở thẻ ra là gặp ngay việc xử được nhanh nhất.",
      "Máy chấm lại mọi khoản mỗi lần mở thẻ, nên đơn tạo SAU khi tiền về (khách chuyển cọc trước) vẫn được gợi ý. Máy chỉ gợi ý đơn trong các cơ sở bạn được xem.",
      "Máy chỉ tính số điện thoại khi nội dung ghi đủ 9 số cuối (viết liền hay cách nhóm đều được); 4 số trần trong nội dung không còn được tính, trừ khi khách gõ đúng khuôn GNPF + 4 số mà mã QR in ra. Tên khách so theo từng chữ, cả cụm họ tên mới là trùng tên. Số tiền một mình không bao giờ đủ để gợi ý — nhiều đơn cùng giá.",
      '"Khớp chắc" là khi nội dung ghi đủ số điện thoại hoặc mã đơn của ĐÚNG MỘT đơn đang nợ, khoản không vượt số còn nợ, chưa ai báo đơn khác, đơn không có phiếu ghi tay cùng số tiền đang chờ nối, và đơn không còn phiếu tiền mặt chờ duyệt cùng số tiền. Hai đơn cùng số điện thoại (một khách hai đơn) thì không còn chắc. Mã đơn phải đứng trọn: nội dung ghi AT-2610-1129 không tính là mã của đơn AT-2610-112.',
      'Đơn còn phiếu tiền mặt chờ duyệt đúng bằng khoản chuyển khoản: thường là phiếu cọc ghi tiền mặt mà khách lại chuyển khoản. Xem thẻ, nhận khoản vào đơn và bác phiếu tiền mặt đó — duyệt cả hai là đơn bị ghi thu hai lần.',
      'Nút "✓ Nhận … khoản khớp chắc" mở hộp bày từng khoản (số tiền, nội dung tô vàng, đơn sẽ nhận, dấu hiệu) trước khi bấm. Máy soát lại từng khoản lúc bấm: khoản vừa được người khác nhận, đơn vừa thu đủ, hai khoản cùng một đơn mà cộng lại vượt số còn nợ… thì bỏ qua và hộp ghi lý do từng khoản. Có hơn 200 khoản thì hộp tự gửi từng đợt 200; một đợt lỗi giữa chừng thì hộp vẫn báo số khoản các đợt trước đã nhận.',
      'Mỗi lần trang hiện 50 khoản. Khoản có người báo luôn nằm trong lượt hiện đầu tiên dù tiền về đã lâu; muốn xem các khoản còn lại thì bấm "Xem thêm".',
      'Có người gửi đề xuất là cả phòng Kế toán nhận chuông "Có khoản tiền chờ kế toán duyệt" kèm số tiền và mã đơn — bấm vào chuông là mở thẳng thẻ này.',
      'Khoản có người báo cũng hiện bên phần mềm Kế toán, ở mục "Phiếu duyệt thu", kèm nội dung chuyển khoản, giờ tiền về và tên người báo. Duyệt hay bác ở bên nào trước thì bên kia tự rút khoản đó ra — chỉ cần xử một lần.',
      'Bác một đề xuất báo nhầm đơn (ở đây hay bên phần mềm Kế toán) thì khoản tiền về lại thẻ này như chưa ai báo, người báo nhận chuông kèm lý do. Ai khác cũng báo khoản đó vào CÙNG đơn thì đề xuất của họ đóng theo, kèm cùng lý do. Máy không gợi ý lại đơn vừa bị bác cho khoản đó.',
      'Bill người báo gửi KÈM lúc bấm "Nhờ kế toán xác nhận" thì hiện cả ở đây lẫn bên phần mềm Kế toán. Bill đính BỔ SUNG sau thì hiện ngay ở thẻ này, còn bên phần mềm Kế toán chưa thấy — muốn nhìn bill đó thì duyệt ở đây.',
      "Nội dung khách gõ để nguyên văn, không tóm tắt — đó là manh mối gốc để tra ra đơn.",
      'Nút chính gọi thẳng tên đơn ("✓ Nhận vào đơn ABC") nên đọc đúng mã trên nút rồi hãy bấm, không phải rê mắt lên dải tín hiệu đối chiếu lại.',
      'Khoản có người báo thì bấm nút chính sẽ đi đường trả lời người báo, để họ biết việc mình gửi đã xong — khác với tự nối tay.',
      'Ba nút giữ nguyên thứ tự ở mọi thẻ, kể cả khi thẻ này thiếu tín hiệu thẻ kia có.',
      '"Không phải của mình" chỉ gác khoản sang một bên, khoản vẫn nằm trong sổ ngân hàng và hoàn tác được ngay trên lời nhắc vừa hiện.',
    ],
    statuses: [
      { label: "🙋 đề xuất", meaning: "Có người bên cửa hàng đã báo khoản này thuộc đơn nào." },
      {
        label: "✅ Khớp chắc",
        meaning: "Nội dung chỉ đích danh đúng một đơn đang nợ (đủ số điện thoại hoặc mã đơn); nhận được cả loạt bằng nút trên đầu.",
      },
      { label: "🤖 Máy gợi ý", meaning: "Máy dò ra đơn có dấu hiệu khớp, cần người đọc nhãn dấu hiệu rồi xác nhận." },
      { label: "Máy gợi ý lúc tiền về", meaning: "Gợi ý máy ghi lại ngay lúc tiền về (lượt đặt lịch), không kèm nhãn dấu hiệu." },
      {
        label: "📝 phiếu ghi tay chờ nối",
        meaning: "Có người mở sẵn một phiếu cho đơn, chờ nối khoản tiền này vào.",
      },
      { label: "✅ đã nhận", meaning: "Khoản đã gắn vào đơn, không còn phải xử." },
    ],
    blocks: [
      {
        for: ["accounting"],
        title: "Dọn hàng đợi mỗi sáng",
        steps: [
          'Mở thẻ, để nguyên nút lọc "Chưa xử lý" — đó là việc phải làm hôm nay.',
          "Xử từ trên xuống: khoản trên cùng luôn là khoản chắc chắn nhất.",
          'Có dải xanh "… khoản máy khớp chắc": bấm "✓ Nhận … khoản khớp chắc", lướt từng khoản trong hộp rồi bấm "Nhận cả … khoản". Khoản bị bỏ qua thì đọc lý do và xử trên thẻ của nó.',
          'Thẻ có 🙋: bấm "🧾 Xem bill sale gửi" cuối dòng 🙋 để mở ảnh bill, đặt số tiền và giờ trên bill cạnh số tiền, giờ tiền về và nội dung của khoản — khớp thì nhận. Dòng ghi "Chưa có bill": vẫn nhận được, nhưng nên nhắn người báo đính bill trước nếu số tiền hay tên không khớp rõ.',
          'Thẻ có 🙋 hoặc 🤖: đọc nhãn dấu hiệu và phần chữ tô vàng, đọc mã đơn trên nút chính rồi bấm "✓ Nhận vào đơn …". Đơn đúng nằm ở dòng gợi ý thứ hai trở đi thì bấm "Nhận vào đơn này" trên dòng đó.',
          'Thẻ có 📝: phiếu này máy chỉ đoán theo số tiền — đúng đơn thì bấm "🔗 Nối vào phiếu đó", sai thì bấm "Chọn đơn khác".',
          'Thẻ chưa có manh mối: bấm "Chọn đơn khác", gõ mã đơn hay tên khách để tìm phiếu đang chờ rồi nối vào.',
          'Khoản không thuộc cửa hàng: bấm "Không phải của mình", ghi lý do rồi gác lại.',
          'Khoản ngân hàng chưa đẩy sang: bấm "+ Thêm giao dịch ngân hàng", điền số tiền và nội dung rồi thêm.',
          'Xử hết lượt đang hiện mà vẫn còn khoản: bấm "Xem thêm" ở cuối danh sách.',
        ],
        troubles: [
          "Không tra ra đơn nào: để nguyên khoản trong hàng đợi, đừng nối đại vào đơn gần đúng — nối sai thì đơn kia mang tiền không phải của nó.",
          'Dòng gợi ý chỉ có nhãn "Trùng một phần tên" hay "Trùng tên khách": nhiều khách trùng tên, gọi hỏi hoặc đối chiếu số tiền với đơn trước khi nhận.',
          'Dòng gợi ý ghi "khoản này dư …": khách chuyển nhiều hơn đơn còn nợ — nhận vào thì đơn mang tiền thừa, xử phần dư theo đường hoàn tiền.',
          'Khách báo đã chuyển mà không thấy khoản: xem dòng "Sổ ngân hàng nhận khoản mới nhất lúc …" — giờ đó còn trước lúc khách chuyển là sổ chưa lấy số kịp, đợi rồi tải lại.',
          'Bấm nhầm "Không phải của mình": bấm Hoàn tác ngay trên lời nhắc vừa hiện; lỡ tắt rồi thì mở nút lọc "Đã bỏ qua" để đưa lại.',
          'Ô tìm phiếu không ra gì: phiếu phải do người thu mở trên đơn trước, ở đây chỉ nối vào phiếu đã có.',
          "Máy báo khoản đã vào đơn khác: có người vừa xử trước bạn, đọc lời nhắc để biết đơn nào rồi bỏ qua khoản đó.",
          'Bấm "✓ Nhận vào đơn …" mà đơn đó đã có phiếu chuyển khoản ghi tay CÙNG số tiền, đã duyệt, chưa nối khoản ngân hàng nào: máy nối khoản vào chính phiếu ghi tay đó, KHÔNG cộng thêm tiền — lời nhắc ghi "Đã nối vào phiếu chuyển khoản ghi tay đã duyệt của đơn … — tiền của đơn không tăng", tổng đã thu của đơn giữ nguyên là đúng. Khách chuyển hai lần thật cùng số thì đối chiếu sao kê rồi báo quản lý, đừng tự ghi thêm phiếu.',
          'Duyệt đề xuất mà máy báo đơn "đã có phiếu chuyển khoản ghi tay … kế toán đã duyệt" và "KHÔNG ghi tiền": khoản tiền này đã tính vào đơn qua phiếu ghi tay. Đối chiếu sao kê — cùng một khoản thì bác đề xuất. Bên phần mềm Kế toán duyệt khoản này cũng bị chặn y như vậy.',
          'Bấm duyệt mà máy báo "đề xuất đã được xử lý trước đó": khoản này vừa được duyệt hoặc bác bên phần mềm Kế toán — tải lại thẻ là thấy kết quả, đừng nhận lại lần nữa.',
          'Lỡ bác một đề xuất mà đơn đó đúng: khoản đã về lại thẻ này nhưng máy không gợi ý lại đơn đó — nhắn người báo gửi lại (họ dò thấy khoản và bấm "Nhờ kế toán xác nhận" được ngay), rồi duyệt như mọi đề xuất.',
          'Đóng trang hay rớt mạng lúc đang "Nhận cả … khoản": khoản đang nhận dở vẫn được nhận trọn, khoản chưa tới lượt giữ nguyên — mở lại thẻ, bấm nhận tiếp. Không khoản nào bị nhận hai lần.',
          'Thẻ hiện khung chữ đỏ thay vì danh sách: đó là lý do máy không tải được hàng đợi — đọc đúng câu trong khung, không phải hàng đợi đã sạch.',
        ],
      },
      {
        for: ["lead", "exec"],
        title: "Nhìn nhanh tiền chưa gắn được vào đơn",
        steps: [
          'Bấm ô "TIỀN VỀ CHƯA RÕ ĐƠN" ở đầu trang là vào thẳng thẻ này.',
          "Số khoản tồn nhiều và cũ dần là dấu hiệu khâu đối chiếu đang ùn.",
        ],
      },
    ],
  },

  {
    key: "approve",
    label: TAB_LABEL.approve,
    purpose: "Các phiếu do người tại cửa hàng ghi tay, đang chờ xác nhận.",
    where: [
      'Hai thẻ con: "Chờ duyệt" (mở sẵn) và "⚠️ Chờ đối soát" kèm số trong ngoặc.',
      "Ô tìm nhận mã đơn, tên khách hoặc ghi chú.",
      "Bảng có cột Khách hàng · Số tiền · Người thu · Ghi chú · Thời gian.",
      'Cuối dòng: "Xem QR" với phiếu thu chuyển khoản (phiếu hoàn tiền không có nút này — không có mã nào để khách quét), "✓ Duyệt" và "✗ Từ chối".',
      'Phiếu hoàn tiền: cột Ghi chú ghi "Hoàn về: …" — tài khoản nhận tiền CSKH ghi cho khách lúc đề nghị.',
      'Phiếu tiền mặt có kèm chứng từ thì hiện thêm nút xem chứng từ.',
      'Phiếu hoàn đang chờ Kế toán duyệt: cặp nút mờ, dòng chữ nhỏ bên cạnh nói lý do. Nếu chữ ghi "KHÔNG gửi được lên hệ duyệt tập trung", nút "✗ Từ chối" vẫn bấm được, và cuối dòng có thể có thêm nút "Gửi lại".',
      'Phiếu thu chuyển khoản có nút "🧾 Đính bill" (đã có bill thì "🧾 Thay bill"); phiếu đã có bill hiện "🧾 Xem bill" ở cột Ghi chú.',
      'Hộp xác nhận có ô tích "Khách chuyển khoản SAI nội dung".',
      'Duyệt một phiếu làm đơn thu quá số phải thu thì hộp hiện khung đỏ "⚠️ Duyệt phiếu này là đơn thu dư" và nút duyệt đổi thành "Vẫn duyệt dù thu dư".',
      'Dòng chữ nhỏ dưới hàng chip ngày ghi "Lọc theo ngày ghi nhận thanh toán". Thẻ này mở ra là chọn sẵn "Tất cả", để phiếu ghi từ tháng trước không bị giấu.',
    ],
    howItWorks: [
      'Khách mở album mà chưa tải được ảnh chỉ vì tiền còn chờ xác nhận thì kế toán nhận chuông "Khách đã trả, chờ duyệt để mở tải ảnh" kèm mã đơn. Mở thẻ này duyệt phiếu; không thấy phiếu thì tìm khoản ở "Tiền về chưa rõ đơn" hoặc "Phiếu chờ khách chuyển".',
      'Thẻ con "⚠️ Chờ đối soát" là lối tắt vào chính hàng chờ này, không phải hàng riêng: phiếu mang dấu sai nội dung nằm trong CẢ hai thẻ con, nên xử lần lượt từ trên xuống không bỏ sót phiếu nào.',
      'Ô tích "khách chuyển sai nội dung" đặt ở đây vì lúc lập phiếu chưa ai biết khách sẽ gõ gì; tích rồi phiếu vẫn xác nhận được, và sau đó chuyển sang thẻ "Cần xem lại" để dò với sao kê.',
      "Khoản HOÀN TIỀN cũng đi qua đúng hộp này nhưng hộp đổi mặt: tiêu đề nói rõ là chi ra, số tiền hiện dấu trừ, kèm lý do hoàn, ô \"Chuyển khoản hoàn về\" (tài khoản CSKH ghi) và ô \"Ảnh chứng từ chuyển khoản\" để đính lệnh chuyển; ô tích sai nội dung biến mất vì tiền đi ra không có nội dung của khách.",
      'Khoản hoàn tiền phải được Kế toán duyệt trước rồi mới chuyển: lúc Kế toán chưa duyệt, cặp nút mờ kèm câu "Đang chờ Kế toán duyệt khoản hoàn này". Kế toán duyệt xong thì nút mở, bạn chuyển tiền rồi bấm "✓ Duyệt chi"; Kế toán từ chối thì phiếu tự chuyển sang "Đã từ chối" kèm lời Kế toán.',
      'Nếu phiếu hoàn ghi "KHÔNG gửi được lên hệ duyệt tập trung": Kế toán chưa từng nhìn thấy phiếu, nên chờ bao lâu cũng không có câu trả lời. "✓ Duyệt chi" vẫn khoá vì khoản này chưa được Kế toán đồng ý. "✗ Từ chối" thì bấm được, vì từ chối không có tiền ra. Từ chối xong, phần tiền đó được trả lại vào mức có thể hoàn của đơn.',
      'Trả lại một phiếu thì phải ghi lý do — người thu đọc lý do đó để biết sửa gì, "không hợp lệ" trơ trọi là họ phải đi hỏi lại.',
      'Phiếu mà duyệt là đơn thu quá số phải thu thì lần bấm "✓ Duyệt" đầu tiên CHƯA ghi gì: hộp giữ nguyên, khung đỏ nói đơn còn phải thu (hoặc đã thu) bao nhiêu và sẽ dư bao nhiêu. Bấm "Vẫn duyệt dù thu dư" mới ghi tiền, và lần duyệt đó được ghi vào nhật ký của đơn (mục "Nhật ký" trong chi tiết đơn — mục này chỉ hiện với tài khoản được xem nhật ký đơn).',
    ],
    statuses: [
      { label: "Chờ kế toán duyệt", meaning: "Có người ghi tay khoản này, đang chờ xác nhận." },
      {
        label: "⚠️ Duyệt phiếu này là đơn thu dư",
        meaning: 'Duyệt phiếu này thì đơn thu quá số phải thu. Chưa đồng nào được ghi cho tới khi bấm "Vẫn duyệt dù thu dư".',
      },
      {
        label: "⚠️ Sai nội dung CK",
        meaning: "Phiếu mang dấu khách chuyển thiếu hoặc sai nội dung, cần dò với sao kê.",
      },
      { label: "Đã từ chối", meaning: "Phiếu bị trả lại kèm lý do, chưa tính là đã thu." },
      {
        label: "Đang chờ Kế toán duyệt khoản hoàn này",
        meaning: "Phiếu hoàn đã gửi sang Kế toán; cặp nút mờ cho tới khi Kế toán duyệt hoặc từ chối.",
      },
      {
        label: "KHÔNG gửi được lên hệ duyệt tập trung",
        meaning: 'Phiếu hoàn chưa tới được Kế toán. Không chi được; từ chối thì được; báo kỹ thuật để gửi lại.',
      },
    ],
    blocks: [
      {
        for: ["accounting"],
        title: "Xác nhận phiếu ghi tay",
        steps: [
          "Đối chiếu từng phiếu với sao kê: số tiền, thời điểm, nội dung.",
          'Phiếu chuyển khoản còn nghi ngờ: bấm "Xem QR" để đọc lại nội dung đã phát cho khách.',
          'Khớp thì bấm "✓ Duyệt". Sao kê cho thấy khách ghi thiếu hay sai nội dung thì tích ô cảnh báo trong hộp trước khi xác nhận.',
          'Không khớp thì bấm "✗ Từ chối" và ghi lý do cụ thể để người thu biết phải sửa gì.',
          'Khoản hoàn tiền: chờ tới khi nút sáng lại (Kế toán đã duyệt). Đọc kỹ ô "Lý do hoàn tiền", chuyển khoản về đúng tài khoản ở ô "Chuyển khoản hoàn về", chụp màn hình lệnh chuyển đính vào ô "Ảnh chứng từ chuyển khoản" rồi mới bấm "✓ Duyệt chi".',
          'Muốn xử riêng nhóm nghi ngờ thì mở thẻ con "⚠️ Chờ đối soát"; phiếu ở đó vẫn nằm trong hàng chung.',
          'Khách chuyển sai nội dung mà phiếu chưa có bill: xin bill từ người thu, bấm "🧾 Đính bill" rồi chọn ảnh; bấm "🧾 Xem bill" để mở ảnh khi đối chiếu.',
        ],
        troubles: [
          "Khoản hoàn tiền không có lý do kèm theo: hộp báo thẳng bằng chữ đỏ — kiểm lại với người lập trước khi chi.",
          'Hộp ghi "Phiếu không ghi tài khoản nhận": phiếu hoàn lập từ trước khi hộp đề nghị hỏi tài khoản — hỏi lại CSKH, đừng đoán tài khoản.',
          "Ảnh chứng từ không tải lên được: hộp báo lỗi và CHƯA duyệt chi — thử lại với ảnh JPEG hoặc PNG rồi bấm lại.",
          'Phiếu hoàn ghi "KHÔNG gửi được lên hệ duyệt tập trung": đừng chuyển tiền. Báo kỹ thuật để gửi lại; nếu khoản hoàn không còn cần nữa thì bấm "✗ Từ chối" kèm lý do.',
          'Bấm "✗ Từ chối" phiếu hoàn mà báo "vừa được gửi lại lên Kế toán": phiếu đã sang Kế toán, chờ Kế toán quyết rồi mới xử lý được.',
          'Tích nhầm ô sai nội dung: phiếu vẫn là đã thu, chỉ mang thêm dấu; sang thẻ "Cần xem lại" bấm "✓ Đã rà xong" để gỡ dấu.',
          'Hộp hiện khung đỏ "Duyệt phiếu này là đơn thu dư": phiếu trùng hay ghi nhầm thì bấm "Hủy" rồi "✗ Từ chối" kèm lý do; khách đưa thêm tiền thật thì bấm "Vẫn duyệt dù thu dư" — phần dư hoàn cho khách qua khiếu nại hoàn tiền.',
        ],
      },
      {
        for: ["lead"],
        title: "Theo dõi phiếu chờ của cơ sở",
        steps: [
          'Thẻ này để bạn XEM: bảng có đủ các phiếu ghi tay của cơ sở bạn đang chờ xác nhận, nhưng cuối dòng không có cặp nút "✓ Duyệt" / "✗ Từ chối".',
          "Số trong ngoặc trên tên thẻ là số phiếu đang chờ. Cột Thời gian cho biết phiếu nào chờ lâu bất thường, cột Người thu cho biết ai ghi phiếu đó.",
          "Tìm một phiếu bằng ô tìm: gõ mã đơn, tên khách hoặc ghi chú.",
          'Phiếu chuyển khoản: bấm "Xem QR" để đọc lại mã và nội dung đã phát cho khách.',
          'Phiếu đã có bill: bấm "🧾 Xem bill" ở cột Ghi chú để mở ảnh.',
        ],
        troubles: [
          "Một phiếu chờ lâu bất thường: hỏi người thu xem khách đã trả thật chưa, rồi báo phòng Kế toán kèm mã đơn.",
          "Thấy phiếu ghi sai số tiền hay sai đơn: đừng ghi thêm phiếu mới chồng lên, báo phòng Kế toán kèm mã đơn để họ trả lại phiếu đó.",
          "Không thấy phiếu của một cơ sở: thẻ chỉ hiện phiếu trong các cơ sở bạn được xem.",
        ],
      },
      {
        for: ["exec"],
        title: "Nhìn nhanh hàng phiếu chờ",
        steps: [
          "Số trong ngoặc trên tên thẻ là số phiếu đang chờ xác nhận.",
          "Cột Thời gian cho biết phiếu nào chờ lâu bất thường.",
          'Phiếu hoàn ghi "KHÔNG gửi được lên hệ duyệt tập trung": khi kỹ thuật báo đã sửa xong đường gửi, bấm "Gửi lại" ở cuối dòng. Phiếu quay về "Đang chờ Kế toán duyệt khoản hoàn này".',
          'Lần duyệt nào làm đơn thu dư cũng để lại dòng "Duyệt phiếu thu làm đơn thu dư" trong nhật ký của đơn (mục "Nhật ký" trong chi tiết đơn, chỉ hiện với tài khoản được xem nhật ký đơn): người bấm, số tiền của phiếu, số đơn dư, và lúc bấm đã đọc cảnh báo đỏ hay chưa — kể cả khi phiếu được duyệt ở bàn duyệt của Kế toán.',
        ],
        troubles: [
          'Bấm "Gửi lại" mà báo "chưa vào hàng chết": máy vẫn đang tự gửi lại, không cần bấm — đợi ít phút rồi tải lại trang.',
          'Dòng nhật ký ghi "đã đọc cảnh báo dư …, nhưng lúc ghi vào đơn đã dư …": tiền vào thêm sau lúc phiếu được xem. Đối chiếu lại với khách; phần dư hoàn qua khiếu nại hoàn tiền.',
        ],
      },
    ],
  },

  {
    key: "review",
    label: TAB_LABEL.review,
    purpose: "Phiếu đã thu nhưng còn mang dấu khách chuyển sai nội dung, chờ dò lại với sao kê.",
    where: [
      "Đầu thẻ là dòng nhắc màu vàng nói rõ thẻ này gom phiếu nào.",
      "Ô tìm nhận mã đơn, tên khách hoặc ghi chú.",
      'Bảng có cột Khách hàng · Số tiền · Người thu · Đã duyệt (ai, lúc nào) · Ghi chú.',
      'Cuối dòng: nút "✓ Đã rà xong".',
      'Dòng chữ nhỏ dưới hàng chip ngày ghi "Lọc theo ngày ghi nhận thanh toán".',
    ],
    howItWorks: [
      "Phiếu vào đây khi có người tích dấu sai nội dung lúc xác nhận. Trước khi có thẻ này, dấu đó biến mất ngay lúc phiếu được xác nhận nên không ai quay lại dò nữa.",
      'Phiếu ở đây ĐÃ là tiền đã thu — thẻ này không giữ tiền lại, chỉ giữ việc dò sao kê.',
      '"✓ Đã rà xong" chỉ gỡ dấu, không đụng gì tới khoản tiền; gỡ xong phiếu rời thẻ này và nằm lại trong sổ đã thu như mọi phiếu khác.',
      "Cột Đã duyệt cho biết ai xác nhận và lúc nào, để hỏi lại đúng người khi số liệu vênh.",
    ],
    statuses: [
      {
        label: "⚠️ Sai nội dung CK",
        meaning: "Dấu còn treo trên phiếu: chưa ai dò lại khoản này với sao kê.",
      },
    ],
    blocks: [
      {
        for: ["accounting"],
        title: "Dò nốt phiếu còn treo dấu",
        steps: [
          "Mở sao kê ngân hàng của khoảng ngày tương ứng.",
          "Đối chiếu từng phiếu: số tiền và thời điểm phải tìm được một khoản thật trong sao kê.",
          'Khớp rồi bấm "✓ Đã rà xong" để gỡ phiếu khỏi hàng.',
          "Không tìm được khoản tương ứng: để nguyên dấu và báo quản lý trực tiếp — đây đúng là loại chênh lệch thẻ này sinh ra để giữ lại.",
        ],
        troubles: [
          'Gỡ nhầm một phiếu: phiếu rời thẻ này nhưng vẫn nằm trong sổ đã thu, tra lại ở thẻ "Đã thu" theo tên khách.',
          "Thẻ trống trơn: không còn phiếu nào treo dấu, không phải lỗi.",
        ],
      },
      {
        for: ["lead", "exec"],
        title: "Nhìn nhanh phần còn phải dò",
        steps: [
          "Số trong ngoặc trên tên thẻ là số phiếu chưa dò xong.",
          "Số này tồn lâu nghĩa là việc dò sao kê đang bị bỏ lại phía sau.",
        ],
      },
    ],
  },

  {
    key: "bank",
    label: TAB_LABEL.bank,
    purpose: "Sổ tra cứu mọi giao dịch ngân hàng, dùng khi cần lần lại một khoản cũ.",
    where: [
      'Bốn ô số nhỏ đầu thẻ: thu hôm nay · tổng tiền về · chưa khớp · đã xác nhận hôm nay.',
      'Nút "+ Thêm giao dịch ngân hàng" để gõ tay một khoản.',
      'Ba nút lọc: "Chưa khớp" (mở sẵn), "Đã khớp", "Tất cả".',
      'Cạnh đó: ô tích "Hiện cả khoản đã bỏ qua" — mặc định tắt.',
      'Hai ô lọc riêng: lọc theo số tiền (chấp nhận lệch chút ít) và tìm trong nội dung chuyển khoản.',
      'Mỗi dòng có ba nhãn xếp dọc bên phải, và nút "Liên kết" với khoản chưa khớp.',
      'Dòng chữ nhỏ dưới hàng chip ngày ghi "Lọc theo ngày giao dịch ngân hàng".',
    ],
    howItWorks: [
      "Đây là nơi TRA CỨU, không phải nơi làm việc hằng ngày — việc dọn tiền về mỗi ngày nằm ở thẻ hàng đợi.",
      'Bốn ô số nhỏ đếm MỌI THỜI GIAN, không đổi theo khoảng ngày ở đầu trang; dòng chữ dưới mỗi ô nói rõ điều đó. Danh sách bên dưới thì có theo khoảng ngày, nên hai bên vênh nhau là đúng.',
      'Khoản đã gác mặc định ẩn đi và hiện mờ khi bật lên: khoản đã kết luận mà nằm lẫn trong sổ thì đọc lướt sẽ tưởng còn phải xử.',
      'Hộp "Liên kết giao dịch" xếp phiếu chờ theo mức khớp và gắn nhãn cho phiếu khớp tốt; danh sách phiếu trong hộp cố ý không bó theo khoảng ngày, vì tiền về hôm nay thường trả cho phiếu mở từ mấy tuần trước.',
      "Lọc theo số tiền và theo nội dung là đường nhanh nhất khi khách gửi ảnh chụp tin nhắn chuyển tiền.",
    ],
    statuses: [
      { label: "Đã khớp", meaning: "Giao dịch đã ghép đúng vào một phiếu thu." },
      { label: "Chưa khớp", meaning: "Tiền về nhưng chưa biết của đơn nào." },
      { label: "Chờ xác nhận", meaning: "Giao dịch đã ghép, phiếu tương ứng chưa được xác nhận." },
      { label: "Đã xác nhận", meaning: "Cả giao dịch lẫn phiếu đều xong." },
      { label: "Tạo tay", meaning: "Khoản do người gõ vào, không do ngân hàng báo sang." },
      { label: "Đã bỏ qua", meaning: "Khoản đã gác lại; dòng hiện mờ và có nút đưa lại hàng đợi." },
    ],
    blocks: [
      {
        for: ["accounting"],
        title: "Lần lại một khoản cũ",
        steps: [
          "Có ảnh chụp tin nhắn của khách: gõ số tiền vào ô lọc số tiền — nhanh hơn dò theo ngày.",
          "Nhớ mang máng nội dung: gõ một mẩu chữ vào ô tìm nội dung.",
          'Cần cả khoản đã gác: bật ô tích "Hiện cả khoản đã bỏ qua".',
          'Khoản chưa khớp mà đã tra ra đơn: bấm "Liên kết", chọn phiếu trong danh sách xếp sẵn.',
          'Khoản gác nhầm: bấm "Đưa lại vào hàng đợi" trên chính dòng đó.',
        ],
        troubles: [
          'Bốn ô số không khớp danh sách bên dưới: đúng như vậy — ô số đếm mọi thời gian, danh sách theo khoảng ngày đang chọn.',
          "Không thấy khoản cần tìm: nới khoảng ngày ở đầu trang, hoặc bỏ bớt ô lọc số tiền.",
        ],
      },
      // KHÔNG thêm khối cho tầng điều hành ở thẻ này: sổ ngân hàng cố ý hẹp
      // hơn mọi thẻ khác, chỉ nghề kế toán mở được (quyết định 0067). Bản
      // trước có một khối như vậy và nó chưa từng hiện ra với ai — chữ chết
      // thì không ai soát lại khi nó lạc hậu. Muốn nói với tầng điều hành thì
      // nói ở thẻ họ mở được.
    ],
  },

  {
    key: "accounting",
    label: TAB_LABEL.accounting,
    purpose: "Những việc tiền của đơn phải sửa tay bên phần mềm Kế toán, vì phần mềm Kế toán đã chốt số của đơn đó.",
    where: [
      'Tên thẻ kèm số trong ngoặc: số việc còn chờ xử lý.',
      'Đầu thẻ: hai nút lọc "Chưa xử lý" và "Đã xử lý", bên phải là số việc đang hiện.',
      "Mỗi dòng: mã đơn · tên khách · số tiền ở góc phải, rồi một câu nói việc phải làm, dòng chữ nhỏ ghi chi tiết và lúc ghi.",
      'Cuối mỗi dòng chưa xử lý: nút "Đã xử lý bên Kế toán". Bấm vào sẽ hiện ô ghi chú cùng hai nút "Xác nhận" và "Thôi".',
      "Thẻ này không dùng khoảng ngày, nên hàng chip ngày biến mất khi mở nó.",
    ],
    howItWorks: [
      "Khi một đơn đã được ghi doanh thu bên phần mềm Kế toán, số tiền của đơn đó bên ấy được chốt lại. Từ lúc đó, mọi thay đổi làm GIẢM tiền của đơn không tự đi sang được — chúng hiện ở đây để kế toán sửa tay.",
      "Có năm loại việc: đơn đã huỷ · hoàn tiền cho khách · tiền gói hoặc phát sinh giảm · khoản phát sinh đã báo nay bị huỷ · tin gửi sang bị từ chối.",
      'Khoản phát sinh đã báo mà đổi tiền ngay trên dòng — ví dụ bật thuế sau khi khách trả đủ, hay đổi ưu đãi — cũng được tính: giảm thì hiện ở đây với chữ "Tiền phát sinh đã báo giảm", tăng thì tự sang thành đơn phụ "Chênh lệch phát sinh".',
      "Tiền TĂNG thêm sau khi đã ghi doanh thu thì không hiện ở đây: nó tự sang bên Kế toán thành một đơn phụ mang mã đơn gốc kèm đuôi -PS.",
      'Bấm "Đã xử lý bên Kế toán" chỉ đánh dấu ở trang này — không sửa gì bên phần mềm Kế toán. Sửa bên đó trước, đánh dấu ở đây sau.',
      'Dòng đã đánh dấu chuyển sang "Đã xử lý", giữ tên người bấm, giờ bấm và ghi chú để tra lại.',
    ],
    statuses: [
      { label: "Chưa xử lý", meaning: "Việc còn chờ kế toán sửa bên phần mềm Kế toán." },
      { label: "Đã xử lý", meaning: "Đã có người sửa bên Kế toán và đánh dấu; dòng ghi ai làm, lúc nào." },
    ],
    blocks: [
      {
        for: ["accounting"],
        title: "Xử lý một việc",
        steps: [
          'Mở thẻ, để nút lọc ở "Chưa xử lý".',
          "Đọc câu nói việc phải làm và số tiền của dòng.",
          "Sang phần mềm Kế toán, tìm đúng mã đơn đó và sửa: huỷ đơn, hoặc ghi giảm đúng số tiền.",
          'Quay lại đây, bấm "Đã xử lý bên Kế toán", ghi số chứng từ bên Kế toán vào ô ghi chú rồi bấm "Xác nhận".',
        ],
        troubles: [
          'Bấm "Xác nhận" mà báo việc đã có người đánh dấu: một người khác vừa xử lý xong, mở "Đã xử lý" để xem ai làm.',
          "Dòng ghi tin gửi sang bị từ chối: đọc lý do ở dòng chữ nhỏ, nhập tay đơn đó bên phần mềm Kế toán rồi mới đánh dấu.",
          "Không tìm thấy mã đơn bên phần mềm Kế toán: hỏi quản lý trực tiếp trước khi đánh dấu.",
        ],
      },
      {
        for: ["exec"],
        title: "Theo dõi việc tồn",
        steps: [
          "Nhìn số trong ngoặc ở tên thẻ: số việc kế toán còn phải sửa tay.",
          'Mở "Đã xử lý" để xem ai đã xử lý việc nào, lúc nào, ghi chú gì.',
        ],
      },
    ],
  },
];
